import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Server, Users, LogOut, ExternalLink, Settings, ClipboardList, Key, Star, Menu, X } from 'lucide-react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

const NAV = [
  { to: '/admin/providers',   icon: Server,         label: 'Anbieter' },
  { to: '/admin/api-keys',    icon: Key,            label: 'API-Schlüssel' },
  { to: '/admin/reviews',     icon: Star,           label: 'Bewertungen' },
  { to: '/admin/team',        icon: Users,          label: 'Team',          adminOnly: true },
  { to: '/admin/audit',       icon: ClipboardList,  label: 'Protokoll',     adminOnly: true },
  { to: '/admin/settings',    icon: Settings,       label: 'Einstellungen' },
];

function SidebarContent({ user, onNav, handleLogout }) {
  return (
    <>
      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAV.filter(n => !n.adminOnly || user?.role === 'admin').map(({ to, icon: Icon, label }) => (
          <NavLink key={to} to={to} onClick={onNav}
            data-testid={`nav-${label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800 border border-transparent'
              }`
            }>
            <Icon className="w-4 h-4 flex-shrink-0" />
            {label}
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      <div className="px-3 py-4 border-t border-slate-800 space-y-1">
        <a href="/" target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-slate-500 hover:text-slate-300 hover:bg-slate-800 transition-colors">
          <ExternalLink className="w-4 h-4" />
          Website
        </a>
        <button onClick={handleLogout} data-testid="admin-logout"
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors">
          <LogOut className="w-4 h-4" />
          Abmelden
        </button>
        <div className="px-3 py-2 rounded-lg bg-slate-800/50 mt-1">
          <p className="text-xs font-medium text-white truncate">{user?.name}</p>
          <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${
            user?.role === 'admin' ? 'bg-cyan-500/15 text-cyan-400' : 'bg-slate-700 text-slate-400'
          }`}>
            {user?.role === 'admin' ? 'Administrator' : 'Editor'}
          </span>
        </div>
      </div>
    </>
  );
}

export function AdminLayout({ children }) {
  const { user, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/admin/login'); };
  const logoEl = (
    <a href="/" className="flex items-center gap-1 select-none">
      <span className="text-base font-extrabold text-white uppercase" style={{ fontFamily: 'Outfit, sans-serif' }}>Hosting-</span>
      <span className="text-base font-extrabold text-cyan-400 uppercase" style={{ fontFamily: 'Outfit, sans-serif' }}>Community</span>
    </a>
  );

  return (
    <div className="min-h-screen bg-[#020817]">

      {/* ── Mobile top bar ── */}
      <div className="md:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-3 bg-[#0b1121] border-b border-slate-800">
        {logoEl}
        <button onClick={() => setMobileOpen(true)} data-testid="admin-mobile-menu-btn"
          className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors">
          <Menu className="w-5 h-5" />
        </button>
      </div>

      {/* ── Mobile drawer overlay ── */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden" aria-hidden="true">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-[#0b1121] border-r border-slate-800 flex flex-col">
            <div className="px-5 py-5 border-b border-slate-800 flex items-center justify-between">
              {logoEl}
              <button onClick={() => setMobileOpen(false)} className="p-1.5 rounded-lg text-slate-500 hover:text-white transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <SidebarContent user={user} onNav={() => setMobileOpen(false)} handleLogout={handleLogout} />
          </aside>
        </div>
      )}

      {/* ── Desktop sidebar ── */}
      <aside className="hidden md:flex w-56 bg-[#0b1121] border-r border-slate-800 flex-col fixed h-full z-10">
        <div className="px-5 py-5 border-b border-slate-800">
          {logoEl}
          <span className="text-xs text-slate-600 mt-0.5 block">Admin</span>
        </div>
        <SidebarContent user={user} onNav={() => {}} handleLogout={handleLogout} />
      </aside>

      {/* ── Main content ── */}
      <main className="md:ml-56 min-h-screen">
        {children}
      </main>
    </div>
  );
}
